import { ArrowRight, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Container } from '../components/ui/Container';
import { PageHero } from '../components/ui/PageHero';
import { signIn, useAuth } from '../lib/auth';
import { usePageMeta } from '../lib/usePageMeta';

export function Login(): JSX.Element {
  const navigate = useNavigate();
  const { isAuthenticated, mustChangePassword } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const emailRef = useRef<HTMLInputElement | null>(null);

  usePageMeta();

  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      navigate(mustChangePassword ? '/change-password' : '/portal', { replace: true });
    }
  }, [isAuthenticated, mustChangePassword, navigate]);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    const trimmed = email.trim().toLowerCase();
    if (!trimmed.includes('@')) {
      setError('Please enter the email address on file with the association.');
      return;
    }
    if (!password) {
      setError('Please enter your password.');
      return;
    }
    setSubmitting(true);
    try {
      await signIn(trimmed, password);
      // Routing handled by the isAuthenticated effect above.
    } catch (err) {
      console.error(err);
      setError(
        'That email and password did not match. Try again, or contact the board if you have lost access.',
      );
      setSubmitting(false);
    }
  }

  return (
    <>
      <PageHero
        eyebrow="Owners Portal"
        title="Owner Login"
        lead="Sign in with the email address on file with the association. If this is your first time, your password is Welcome3873. You'll be prompted to change it."
      />

      <section className="section bg-brand-white">
        <Container>
          <div className="mx-auto max-w-md rounded-2xl border border-brand-surface bg-brand-white p-8 shadow-card md:p-10">
            {error ? (
              <div
                role="alert"
                className="mb-6 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
              >
                {error}
              </div>
            ) : null}

            <form onSubmit={onSubmit} noValidate>
              <label className="block">
                <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-brand-body-medium">
                  Email
                </span>
                <input
                  ref={emailRef}
                  type="email"
                  name="email"
                  autoComplete="email"
                  inputMode="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={submitting}
                  className="mt-2 block h-11 w-full rounded-md border border-brand-surface bg-brand-white px-3 text-base text-brand-charcoal placeholder:text-brand-body-medium focus:border-brand-yellow focus:outline-none focus:ring-2 focus:ring-brand-yellow"
                  placeholder="your.email@example.com"
                />
              </label>

              <label className="mt-5 block">
                <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-brand-body-medium">
                  Password
                </span>
                <input
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={submitting}
                  className="mt-2 block h-11 w-full rounded-md border border-brand-surface bg-brand-white px-3 text-base text-brand-charcoal placeholder:text-brand-body-medium focus:border-brand-yellow focus:outline-none focus:ring-2 focus:ring-brand-yellow"
                  placeholder="Your password"
                />
              </label>

              <button
                type="submit"
                disabled={submitting}
                className="mt-6 inline-flex h-12 w-full items-center justify-center gap-2 rounded-md bg-brand-yellow px-5 text-sm font-semibold text-brand-charcoal transition-colors hover:bg-brand-yellow-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-yellow focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" aria-hidden="true" />
                    Signing in...
                  </>
                ) : (
                  <>
                    Sign in
                    <ArrowRight size={16} aria-hidden="true" />
                  </>
                )}
              </button>

              <p className="mt-6 text-xs leading-relaxed text-brand-body-medium">
                First time? Your initial password is <strong>Welcome3873</strong>. You will be asked
                to set a new one immediately. Lost access?{' '}
                <a
                  href="/contact"
                  className="font-semibold text-brand-charcoal underline decoration-brand-yellow-deep/40 underline-offset-4 hover:decoration-brand-yellow-deep"
                >
                  Contact the board
                </a>
                .
              </p>
            </form>
          </div>
        </Container>
      </section>
    </>
  );
}
